import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Modal, Text, Flex, ModalProps, Button, ArrowDownIcon, Skeleton, AutoRenewIcon } from 'vvs-uikit'
import { useStakeNftsAndBoostFarm, useMoveBoost } from 'state/farmBoost/useFarmBoostMutations'
import { useUserBoostedFarmsInfo, useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import useToast from 'hooks/useToast'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import { useDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { getFarmVersionFromPid } from 'state/farms/farmsPidToVersion'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TokenPairSelector } from 'components/TokenPairSelector'
import { useFarmBoostPairOptions } from 'hooks/useFarmBoostPairOptions'
import { TokenPairImage } from 'components/TokenImage'
import { TokenPairText, TokenWrapper } from 'views/BoostCalculator/styles'
import { CurrentBoostMultiplierRow, PreviewBoostMultiplierRow } from 'views/xVvs/component/BoostFarmDetails'
import { MAX_XVVS_BOOST_FACTOR, MAX_NUM_OF_BOOST_FARM, MAX_TOTAL_BOOST_FACTOR } from 'config/constants/farmBoost'
import useInvalidateBoostedFarms from 'views/Farms/hooks/useInvalidateBoostedFarms'
import type { DeserializedFarm } from 'state/types'

interface BoostFarmModalProps extends ModalProps {
  step?: BOOST_FARM_STEP
  currentFarmId?: number
  targetFarmId?: number
}

const StyledModal = styled(Modal)<ModalProps>`
  > div {
    max-height: 80vh;
    ${({ theme }) => theme.mediaQueries.sm} {
      width: 570px;
    }
  }
`

const Announcement = styled.div`
  width: 100%;
  padding: 18px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.boostlightAquaBackground};
  color: ${({ theme }) => theme.colors.text};
`

const TokenPairSelectorWrapper = styled.div`
  border-top: 2px solid ${({ theme }) => theme.colors.lightBeige};
`

const SelectPair: React.FC<{
  setCurrentStep: (step: BOOST_FARM_STEP) => void
  setState: (state: { currentFarmId: number; targetFarmId: number }) => void
  boostedFarms: number[]
  currentFarmId: number
  targetFarmId: number
}> = ({ setCurrentStep, setState, boostedFarms, currentFarmId, targetFarmId }) => {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<number>()
  const [farmBoostUserInfo, isGetFarmUserInfoLoaded] = useUserBoostedFarmsInfo(currentFarmId)
  const boostingNfts = farmBoostUserInfo?.boostingNfts ?? []

  const currentMaxBoostFactor = useMemo(() => {
    return isGetFarmUserInfoLoaded && (boostingNfts?.length > 0 ? MAX_TOTAL_BOOST_FACTOR : MAX_XVVS_BOOST_FACTOR)
  }, [boostingNfts?.length, isGetFarmUserInfoLoaded])

  const tokenPairOptionsFilterFn = useMemo<(farm: DeserializedFarm) => boolean>(
    () =>
      currentFarmId
        ? (farm) => farm.pid !== currentFarmId && !boostedFarms?.includes(farm.pid)
        : (farm) => boostedFarms?.includes(farm.pid),
    [currentFarmId, boostedFarms],
  )
  const { pairOptions } = useFarmBoostPairOptions({
    boostMultiplier: currentMaxBoostFactor,
    filterFn: tokenPairOptionsFilterFn,
  })

  const currentFarm = useDeserializedFarmFromPid(currentFarmId, getFarmVersionFromPid(currentFarmId))
  return (
    <Flex flexDirection="column" gap="24px">
      {!currentFarm ? (
        <Text fontWeight="600" fontSize="16ox" lineHeight="24px">
          {t('WHICH BOOSTED FACTOR YOU WANT TO SWITCH FROM:')}
        </Text>
      ) : (
        <Flex flexDirection="column" gap="16px">
          <Text fontWeight="600" fontSize="16ox" lineHeight="24px">
            {t('FROM CURRENT BOOSTED FARM')}
          </Text>
          <Flex alignItems="center" gap="12px">
            <TokenWrapper>
              <TokenPairImage
                primaryToken={currentFarm.token}
                secondaryToken={currentFarm.quoteToken}
                variant="inverted"
                width={40}
                height={40}
              />
            </TokenWrapper>
            <TokenPairText>{currentFarm.lpSymbol.split(' ')[0]}</TokenPairText>
          </Flex>
          <ArrowDownIcon width="32px" />
          <Text fontWeight="600" fontSize="16ox" lineHeight="24px">
            {t('CHANGE TO')}
          </Text>
        </Flex>
      )}

      <TokenPairSelectorWrapper>
        <TokenPairSelector
          limit={1}
          defaultValues={[]}
          onSelect={(ids) => {
            setSelectedId(ids[0])
          }}
          options={pairOptions}
        />
      </TokenPairSelectorWrapper>
      <Announcement>{t('You will stop earning the current boosted rewards by changing your farm.')}</Announcement>
      <Button
        variant="primary"
        scale="lg"
        style={{ width: '100%' }}
        disabled={isNilOrUndef(selectedId)}
        onClick={() => {
          setState({
            currentFarmId: currentFarmId || selectedId,
            targetFarmId: targetFarmId || selectedId,
          })
          setCurrentStep(BOOST_FARM_STEP.confirm)
        }}
      >
        {t('Continue')}
      </Button>
    </Flex>
  )
}

const Confirm: React.FC<{
  onConfirm: () => void
  currentFarmId?: number
  targetFarmId?: number
}> = ({ onConfirm, currentFarmId, targetFarmId }) => {
  const { t } = useTranslation()
  const { toastError } = useToast()
  const addTransaction = useTransactionAdder()
  const { pairOptions } = useFarmBoostPairOptions({
    boostMultiplier: MAX_XVVS_BOOST_FACTOR,
  })
  const currentFarm = useDeserializedFarmFromPid(currentFarmId, getFarmVersionFromPid(currentFarmId))
  const currentFarmRef = useRef(currentFarm)
  currentFarmRef.current = currentFarm
  const targetFarm = useDeserializedFarmFromPid(targetFarmId, getFarmVersionFromPid(targetFarmId))
  const targetFarmRef = useRef(targetFarm)
  targetFarmRef.current = targetFarm
  const currentFarmOption = pairOptions.find((option) => option.id === currentFarmId)
  const targetFarmOption = pairOptions.find((option) => option.id === targetFarmId)
  const { mutateAsync: boostFarm, isLoading: isBoosting } = useStakeNftsAndBoostFarm()
  const { mutateAsync: moveBoost, isLoading: isMoving } = useMoveBoost()
  const { onInvalidatingBoostedFarms } = useInvalidateBoostedFarms()

  const [farmBoostUserInfo] = useUserBoostedFarmsInfo(currentFarmId)
  const boostingNfts = farmBoostUserInfo?.boostingNfts ?? []

  const handleConfirm = useCallback(async () => {
    try {
      if (isNilOrUndef(currentFarmId)) {
        const { tx } = await boostFarm({
          farmId: targetFarmId,
          nfts: [],
        })
        addTransaction(tx, {
          title: t('Farm Boosted'),
          summary: t('Boost %lpSymbol% farm', {
            lpSymbol: targetFarmRef.current.lpSymbol,
          }),
        })
      } else {
        const { tx } = await moveBoost({
          oldPid: currentFarmId,
          newPid: targetFarmId,
        })
        addTransaction(tx, {
          title: t('Switched To Boost Another Farm'),
          summary: t('Switch boost farm from %from% to %to%', {
            from: currentFarmRef.current.lpSymbol,
            to: targetFarmRef.current.lpSymbol,
          }),
        })
      }
      onInvalidatingBoostedFarms()
      onConfirm()
    } catch (e) {
      toastError(t('Transaction Failed'), e?.data?.message || e?.message) // smart contract msg || metamask msg
    }
  }, [
    currentFarmId,
    onInvalidatingBoostedFarms,
    onConfirm,
    boostFarm,
    targetFarmId,
    addTransaction,
    t,
    moveBoost,
    toastError,
  ])

  return (
    <Flex flexDirection="column" gap="24px">
      <Flex flexDirection="column" gap="16px">
        {currentFarm ? (
          <>
            <Text fontWeight="600" fontSize="16ox" lineHeight="24px">
              {t('CURRENT BOOSTED FARM')}
            </Text>
            <CurrentBoostMultiplierRow
              farm={currentFarm}
              vvsRewardsApr={currentFarmOption?.vvsRewardsApr}
              tokenRewardsApr={currentFarmOption?.tokenRewardsApr}
              lpRewardsApr={currentFarmOption?.lpRewardsApr}
              showApplyToBtn={false}
            />
            <ArrowDownIcon width="32px" />
          </>
        ) : null}
        <Text fontWeight="600" fontSize="16ox" lineHeight="24px">
          {t('NEW BOOSTED FARM')}
        </Text>
        <PreviewBoostMultiplierRow
          farm={targetFarm}
          vvsRewardsApr={targetFarmOption?.vvsRewardsApr}
          tokenRewardsApr={targetFarmOption?.tokenRewardsApr}
          lpRewardsApr={targetFarmOption?.lpRewardsApr}
          showApplyToBtn={false}
          boostingNfts={boostingNfts?.map((boostingNft) => boostingNft[1]) as []}
        />
      </Flex>
      {currentFarm && (
        <Announcement>{t('You will stop earning the current boosted rewards by changing your farm.')}</Announcement>
      )}
      <Button
        onClick={handleConfirm}
        isLoading={isBoosting || isMoving}
        endIcon={isBoosting || isMoving ? <AutoRenewIcon color="currentColor" spin /> : null}
        variant="primary"
        scale="lg"
        style={{ width: '100%' }}
      >
        {t('Continue')}
      </Button>
    </Flex>
  )
}

enum BOOST_FARM_STEP {
  selectPair = 'selectPair',
  confirm = 'confirm',
  loading = 'loading',
}

const BoostFarmModal: React.FC<Omit<BoostFarmModalProps, 'title'>> = ({ currentFarmId, targetFarmId, onDismiss }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const boostedFarms = useUserBoostedFarmsInfos((s) => s.value.boostedFarmsIds)

  const [currentStep, setCurrentStep] = useState(BOOST_FARM_STEP.selectPair)
  const [state, setState] = useState({
    currentFarmId,
    targetFarmId,
  })

  useEffect(() => {
    if (boostedFarms && boostedFarms.length < MAX_NUM_OF_BOOST_FARM && targetFarmId) {
      setCurrentStep(BOOST_FARM_STEP.confirm)
    }
  }, [boostedFarms, setCurrentStep, targetFarmId])

  const renderTitle = useMemo<string>(() => {
    let title = ''
    switch (currentStep) {
      case BOOST_FARM_STEP.selectPair:
        title = t('Boost Another Farm')
        break
      case BOOST_FARM_STEP.confirm:
        title = t('Confirm New Boost')
        break
      default:
        break
    }
    return title
  }, [currentStep, t])

  const renderContent = useMemo<React.ReactNode>(() => {
    let content = <Skeleton height="300px" />
    switch (currentStep) {
      case BOOST_FARM_STEP.selectPair:
        content = (
          <SelectPair
            setCurrentStep={setCurrentStep}
            setState={setState}
            boostedFarms={boostedFarms}
            currentFarmId={state.currentFarmId}
            targetFarmId={state.targetFarmId}
          />
        )
        break
      case BOOST_FARM_STEP.confirm:
        content = (
          <Confirm onConfirm={onDismiss} currentFarmId={state.currentFarmId} targetFarmId={state.targetFarmId} />
        )
        break
      default:
        break
    }
    return content
  }, [currentStep, state, boostedFarms, onDismiss])

  return (
    <StyledModal
      bodyPadding="24px"
      title={renderTitle}
      onDismiss={() => {
        onDismiss()
      }}
      closeIconColor={theme.colors.text}
      onBack={
        currentStep === BOOST_FARM_STEP.selectPair || isNilOrUndef(state.currentFarmId)
          ? undefined
          : () => {
              setState({
                currentFarmId,
                targetFarmId,
              })
              setCurrentStep(BOOST_FARM_STEP.selectPair)
            }
      }
    >
      {renderContent}
    </StyledModal>
  )
}

export default BoostFarmModal
